/**
 * src/lib/hooks/usePushSubscription.ts
 * Permission + subscribe/unsubscribe lifecycle for native browser push.
 * Consumed by SettingsPage's push-notifications toggle. Deliberately
 * separate from NotificationContext (which owns the SSE live-toast
 * connection) — push is opt-in per operator and has its own permission
 * state, while SSE is always-on while a session is authenticated.
 */
import { useCallback, useEffect, useState } from 'react';
import { getVapidPublicKey, subscribeToPush, unsubscribeFromPush } from '@/lib/services/notifications';
import { isPushSupported, urlBase64ToUint8Array } from '@/lib/utils/push';

export type PushSubscriptionStatus = 'unsupported' | 'default' | 'denied' | 'subscribed' | 'unsubscribed';

type UsePushSubscriptionResult = {
  status: PushSubscriptionStatus;
  isBusy: boolean;
  error: string | null;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
};

export function usePushSubscription(): UsePushSubscriptionResult {
  const [status, setStatus] = useState<PushSubscriptionStatus>('default');
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // On mount, work out where things already stand: unsupported browser,
  // permission previously denied, or an existing subscription from a past
  // session (service worker + PushManager state persists across reloads).
  useEffect(() => {
    if (!isPushSupported()) {
      setStatus('unsupported');
      return;
    }

    if (Notification.permission === 'denied') {
      setStatus('denied');
      return;
    }

    navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then((existing) => {
        setStatus(existing ? 'subscribed' : 'unsubscribed');
      })
      .catch(() => {
        setStatus('unsubscribed');
      });
  }, []);

  const subscribe = useCallback(async () => {
    if (!isPushSupported()) {
      setStatus('unsupported');
      return;
    }

    setIsBusy(true);
    setError(null);

    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setStatus(permission === 'denied' ? 'denied' : 'unsubscribed');
        return;
      }

      const vapidPublicKey = await getVapidPublicKey();
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });

      const json = subscription.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        throw new Error('Push subscription is missing required fields.');
      }

      await subscribeToPush({
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      });

      setStatus('subscribed');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not enable push notifications.');
      setStatus('unsubscribed');
    } finally {
      setIsBusy(false);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setIsBusy(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        const endpoint = subscription.endpoint;
        await subscription.unsubscribe();
        await unsubscribeFromPush(endpoint);
      }

      setStatus('unsubscribed');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not disable push notifications.');
    } finally {
      setIsBusy(false);
    }
  }, []);

  return { status, isBusy, error, subscribe, unsubscribe };
}
